import { useEffect, useState } from "react";
import { Warehouse, Building2, Truck, ArrowRight, Loader2 } from "lucide-react";
import api from "../../services/api";

/**
 * HubRouteCard — Phase 3 multi-city scalability fix.
 * ──────────────────────────────────────────────────
 * Visualises the full hub journey for a parcel: origin micro → origin
 * main → linehaul → destination main → destination micro. Renders a
 * compact chain card suitable for the tracking page, order detail
 * dialog, or any "route at a glance" widget.
 *
 * Iteration 62 layout fix — every step is rendered as a single column
 * (icon → caption → name → city) so labels never overlap each other or
 * the next/previous step. Connector arrows live in their own thin
 * columns between steps. Linehaul is rendered as an explicit step with
 * a truck icon (only for inter-city orders) instead of a tiny overlay
 * caption attached to the origin main hub.
 */
export default function HubRouteCard({ trackingId, journey: journeyProp }) {
  const [journey, setJourney] = useState(journeyProp || null);
  const [loading, setLoading] = useState(!journeyProp && !!trackingId);

  useEffect(() => {
    let alive = true;
    if (journeyProp) {
      setJourney(journeyProp);
      setLoading(false);
      return () => { alive = false; };
    }
    if (!trackingId) return undefined;
    setLoading(true);
    api.get(`/track/${trackingId}`)
      .then(({ data }) => { if (alive) setJourney(data?.hub_journey || null); })
      .catch(() => { if (alive) setJourney(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [trackingId, journeyProp]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-4 rounded-2xl border border-slate-200 bg-slate-50"
        data-testid="hub-route-card-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="ml-2 text-xs text-slate-500">Loading route…</span>
      </div>
    );
  }
  if (!journey) return null;

  const {
    origin_micro_hub: oMicro,
    origin_main_hub: oMain,
    destination_main_hub: dMain,
    destination_micro_hub: dMicro,
    intra_hub: intraHub,
    intra_city: intraCity,
  } = journey;

  // Nothing meaningful to show
  if (!oMicro && !oMain && !dMicro && !dMain) return null;

  // Build the ordered hub chain, dropping duplicates so an intra-city
  // booking renders as a single node rather than 4 repeated ones.
  const hubs = [];
  const pushUnique = (n, kind) => {
    if (!n) return;
    const last = hubs[hubs.length - 1];
    if (last && last.hub_id === n.hub_id) return;
    hubs.push({ ...n, kind });
  };
  pushUnique(oMicro, "micro");
  pushUnique(oMain, "main");
  pushUnique(dMain, "main");
  pushUnique(dMicro, "micro");

  // Insert a "linehaul" pseudo-step between two consecutive main hubs in
  // DIFFERENT cities (i.e. inter-city). This becomes its own column so
  // it never visually crashes into the adjacent hub label.
  const steps = [];
  for (let i = 0; i < hubs.length; i++) {
    steps.push({ kind: hubs[i].kind, hub: hubs[i] });
    const next = hubs[i + 1];
    if (next && hubs[i].kind === "main" && next.kind === "main" && hubs[i].hub_id !== next.hub_id) {
      steps.push({ kind: "linehaul", hub: null });
    }
  }

  return (
    <div
      className="rounded-2xl border border-orange-200/70 bg-gradient-to-br from-orange-50/80 via-white to-amber-50/40 p-4 sm:p-5"
      data-testid="hub-route-card"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
          <Truck className="h-4 w-4 text-[#FF6B00]" />
          Parcel route
        </h3>
        <span className="text-[11px] font-semibold tracking-wide uppercase text-slate-500">
          {intraHub ? "Same hub" : intraCity ? "Within city" : "Inter-city"}
        </span>
      </div>

      {/* Mobile: vertical stack. Desktop: horizontal scroll-snap. */}
      <ol
        className="flex flex-col sm:flex-row sm:items-start sm:flex-wrap sm:gap-y-4 gap-3"
        data-testid="hub-route-chain"
      >
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          if (step.kind === "linehaul") {
            return (
              <li
                key={`linehaul-${idx}`}
                className="flex sm:flex-col items-center sm:items-center sm:justify-start gap-3 sm:gap-1.5 flex-shrink-0 sm:w-[110px]"
                data-testid="hub-route-node-linehaul"
              >
                <div className="h-10 w-10 rounded-xl flex items-center justify-center shadow-sm border border-dashed border-orange-300 bg-white text-orange-500">
                  <Truck className="h-4 w-4" />
                </div>
                <div className="flex-1 sm:flex-none sm:text-center min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500">
                    Linehaul
                  </p>
                  <p className="text-xs text-slate-500 leading-tight mt-0.5">
                    Inter-city transit
                  </p>
                </div>
                {!isLast && (
                  <ArrowRight className="hidden sm:block h-4 w-4 text-orange-400 self-center sm:absolute sm:opacity-0" />
                )}
                {!isLast && (
                  <ArrowRight className="sm:hidden h-4 w-4 text-orange-400 rotate-90 self-center" />
                )}
              </li>
            );
          }

          const node = step.hub;
          const isMain = step.kind === "main";
          const Icon = isMain ? Building2 : Warehouse;
          return (
            <li
              key={`${node.hub_id}-${idx}`}
              className="flex sm:flex-col items-center sm:items-center gap-3 sm:gap-1.5 flex-shrink-0 sm:w-[150px]"
              data-testid={`hub-route-node-${step.kind}`}
            >
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center shadow-sm border ${
                isMain
                  ? "bg-[#FF6B00] text-white border-[#FF6B00]"
                  : "bg-white text-[#FF6B00] border-orange-200"
              }`}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 sm:flex-none sm:text-center min-w-0 sm:max-w-[150px]">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {isMain ? "Main hub" : "Micro hub"}
                </p>
                <p
                  className="text-sm font-semibold text-slate-900 leading-tight mt-0.5 sm:line-clamp-2"
                  title={node.name}
                >
                  {node.name}
                </p>
                <p className="text-xs text-slate-500 leading-tight mt-0.5 truncate">{node.city}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
